// src/pages/SetPassword.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Container,
} from "@mui/material";
import {
  Lock,
  Visibility,
  VisibilityOff,
  CheckCircle,
  ArrowBack,
} from "@mui/icons-material";
import { useMutation, useQuery } from "react-query";
import candidateService from "../../api/services/candidate";

function SetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');

  const { isLoading: validating, data: tokenValid } = useQuery(
    ['validateToken', token],
    () => candidateService.validateToken(token),
    {
      enabled: !!token,
      retry: false,
      onError: () => setError('Invalid or expired token')
    }
  );

  const setPasswordMutation = useMutation(
    ({ token, password, confirmPassword }) => 
      candidateService.setPassword(token, password, confirmPassword),
    {
      onSuccess: (data) => {
        if (data.success) {
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        } else {
          setError(data.error);
        }
      },
      onError: (error) => {
        setError(error.message || 'Failed to set password');
      }
    }
  );

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setError('');
    setPasswordMutation.mutate({
      token,
      password: formData.password,
      confirmPassword: formData.confirmPassword
    });
  };

  if (validating) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#F1F5F9",
        }}
      >
        <CircularProgress sx={{ color: "#2563EB" }} />
      </Box>
    );
  }

  if (!token || (tokenValid && !tokenValid.success)) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#F1F5F9",
          py: 4,
          px: 2,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -200,
            right: -200,
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(37, 99, 235, 0.06) 0%, transparent 70%)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -200,
            left: -200,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(37, 99, 235, 0.04) 0%, transparent 70%)",
          }}
        />
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              p: { xs: 3, sm: 5 },
              boxShadow: "0 20px 60px rgba(0,0,0,0.06)",
              position: "relative",
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  bgcolor: "#FEE2E2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <Lock sx={{ color: "#DC2626", fontSize: 36 }} />
              </Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#0F172A",
                  mb: 1,
                }}
              >
                Invalid Link
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#64748B",
                  mb: 3,
                }}
              >
                The password setup link is invalid or has expired.
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/resend-link')}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: "#2563EB",
                  "&:hover": {
                    bgcolor: "#1D4ED8",
                  },
                }}
              >
                Request New Link
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (setPasswordMutation.isSuccess) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#F1F5F9",
          py: 4,
          px: 2,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -200,
            right: -200,
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(37, 99, 235, 0.06) 0%, transparent 70%)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -200,
            left: -200,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(37, 99, 235, 0.04) 0%, transparent 70%)",
          }}
        />
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              p: { xs: 3, sm: 5 },
              boxShadow: "0 20px 60px rgba(0,0,0,0.06)",
              position: "relative",
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  bgcolor: "#DCFCE7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <CheckCircle sx={{ color: "#16A34A", fontSize: 36 }} />
              </Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#0F172A",
                  mb: 1,
                }}
              >
                Password Set Successfully!
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#64748B",
                  mb: 2,
                }}
              >
                Your password has been set. Redirecting to login...
              </Typography>
              <CircularProgress size={24} sx={{ color: "#2563EB" }} />
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#F1F5F9",
        py: 4,
        px: 2,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -200,
          right: -200,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(37, 99, 235, 0.06) 0%, transparent 70%)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -200,
          left: -200,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(37, 99, 235, 0.04) 0%, transparent 70%)",
        }}
      />

      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            p: { xs: 3, sm: 5 },
            boxShadow: "0 20px 60px rgba(0,0,0,0.06)",
            position: "relative",
          }}
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/login')}
            sx={{
              mb: 3,
              color: "#64748B",
              textTransform: "none",
              fontWeight: 500,
              "&:hover": {
                color: "#0F172A",
                bgcolor: "transparent",
              },
            }}
          >
            Back to Login
          </Button>

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#0F172A",
                mb: 0.5,
                letterSpacing: "-0.02em",
              }}
            >
              Set Your Password
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#64748B", fontWeight: 400 }}
            >
              Create a secure password for your account
            </Typography>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="New Password"
              variant="outlined"
              type={showPassword ? "text" : "password"}
              placeholder="Enter new password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              size="medium"
              sx={{
                mb: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "#F8FAFC",
                  "&:hover": {
                    bgcolor: "#F1F5F9",
                  },
                  "&.Mui-focused": {
                    bgcolor: "#FFFFFF",
                    boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.06)",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#64748B",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: "#94A3B8", fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClickShowPassword}
                      edge="end"
                      sx={{ color: "#94A3B8" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: "#94A3B8", display: "block", mb: 2, ml: 1 }}
            >
              Minimum 8 characters
            </Typography>

            <TextField
              fullWidth
              label="Confirm Password"
              variant="outlined"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm your password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              size="medium"
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "#F8FAFC",
                  "&:hover": {
                    bgcolor: "#F1F5F9",
                  },
                  "&.Mui-focused": {
                    bgcolor: "#FFFFFF",
                    boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.06)",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#64748B",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: "#94A3B8", fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClickShowConfirmPassword}
                      edge="end"
                      sx={{ color: "#94A3B8" }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={setPasswordMutation.isLoading}
              sx={{
                py: 1.75,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                bgcolor: "#2563EB",
                "&:hover": {
                  bgcolor: "#1D4ED8",
                  boxShadow: "0 4px 16px rgba(37, 99, 235, 0.3)",
                },
                "&:disabled": {
                  bgcolor: "#93C5FD",
                },
              }}
            >
              {setPasswordMutation.isLoading ? <CircularProgress size={24} sx={{ color: "#FFFFFF" }} /> : "Set Password"}
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}

export default SetPassword;